import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { debounce, pickBy } from "lodash";
import { useDealActions, useDealQueriesSelect, useReportsActions, useReportsQueriesSelector } from "store";
import { formatNumberWithoutRounding } from "utils";

import { DateRangeValue, IInvestorViewTable } from "../../../store/types";
import { ColumnsSetMenu } from "./ColumnsSetMenu/ColumnsSetMenu";
import { StyledStack } from "./StyledStack";

interface Props {
  selectedDealName: string;
  setSelectedDealName: React.Dispatch<React.SetStateAction<string>>;
  selectDateRange: DateRangeValue;
}

export const MainTable: React.FC<Props> = ({ selectedDealName, setSelectedDealName, selectDateRange }) => {
  const { filters } = useDealQueriesSelect();
  const { updateQueries } = useDealActions();
  const summaryTableInvestorView = useDealQueriesSelect().summaryTableInvestorView;

  const { exchangeMethod, currency } = useReportsQueriesSelector();

  const [isAutocompleteListOpen, setIsAutocompleteListOpen] = useState<boolean>(false);
  const [isInputInFocus, setIsInputInFocus] = useState<boolean>(false);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  // const [customMenuOpen, setCustomMenuOpen] = useState<HTMLElement | null>(null);

  const { getFlatDataAction } = useReportsActions();

  // const boundingElement = useRef(null);
  const { action, loading, data: tableData } = useActionHook(getFlatDataAction);
  const debouncedGetTableData = useMemo(() => debounce(action, 300), []);

  const handleRowClick = (row: Record<string, string | number | null>) => {
    const dealName = row.deal;
    if (!dealName) return;
    setSelectedDealName(dealName as string);
  };

  useEffect(() => {
    const columns = [
      ...(summaryTableInvestorView.measures ? summaryTableInvestorView.measures : []),
      ...(summaryTableInvestorView.characteristics ? summaryTableInvestorView.characteristics : [])
    ];
    debouncedGetTableData({
      dateRange: selectDateRange,
      rowBy: "deal",
      groupBy: [],
      currency,
      filters: pickBy(filters, (value) => value.length > 0),
      exchangeMethod,
      columns: [...columns],
      calculatedFields: [...(summaryTableInvestorView.calculatedFields || [])]
    });
    setSelectedDealName("");
  }, [filters, selectDateRange, summaryTableInvestorView]);

  const mapTitles = (title: string) => {
    switch (title) {
      case "deal":
        return "Deal Name";
      case "Adjusted NAV":
        return "NAV";
      default:
        return title;
    }
  };

  const formatCellValue = (value: string | number | null, postfix?: string): string => {
    if (value === null || value === undefined || value === "" || value === "-") {
      return "-";
    }

    const numValue = Number(value);
    const formattedValue = numValue > 1000 ? formatNumberWithoutRounding(numValue, 1) : String(value);

    if (postfix === "%") {
      return `${formattedValue}${postfix}`;
    } else if (postfix) {
      return `${postfix}${formattedValue}`;
    }

    return formattedValue;
  };

  const filteredData = useMemo(() => {
    if (!searchInputValue) return tableData?.rows;
    return tableData?.rows.filter((row) => row.title.toLowerCase().includes(searchInputValue.toLowerCase()));
  }, [tableData, searchInputValue, selectedDealName]);

  return (
    <StyledStack sx={{ gap: "12px", flexGrow: 1, width: "50%" }}>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", position: "relative" }}>
        <Typography sx={{ color: "#071F35", fontSize: "18px", lineHeight: "24px", fontWeight: 500 }}>
          Investment Summary
        </Typography>
        {!loading && (
          <ColumnsSetMenu
            onSubmit={(queries) => updateQueries({ summaryTableInvestorView: queries as IInvestorViewTable })}
            columnsSettings={summaryTableInvestorView}
            sx={{ position: "absolute", right: "-10px", top: "48px", zIndex: 4 }}
          />
        )}
        <Autocomplete
          open={isInputInFocus && isAutocompleteListOpen}
          onInputChange={(_, value) => {
            if (value.length === 0) {
              if (isAutocompleteListOpen) setIsAutocompleteListOpen(false);
            } else {
              if (!isAutocompleteListOpen) setIsAutocompleteListOpen(true);
            }
          }}
          onClose={() => setIsAutocompleteListOpen(false)}
          sx={{
            minWidth: isInputInFocus ? 250 : 150,
            transition: "all 0.1s linear",
            ".MuiInputBase-root": { borderRadius: "20px !important", height: "30px" }
          }}
          forcePopupIcon={false}
          options={filteredData?.map((row) => row.title) || []}
          disableClearable
          value={searchInputValue}
          onChange={(_, value) => {
            setSelectedDealName(value);
            setSearchInputValue(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setSearchInputValue(e.target.value)}
              onFocus={() => setIsInputInFocus(true)}
              onBlur={() => setIsInputInFocus(false)}
              variant="outlined"
              label="Search..."
            />
          )}
        />
      </Stack>
      {loading ? (
        <Skeleton variant="rounded" sx={{ flexGrow: 1 }} />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid #DBE8F5",
            borderBottom: "none"
          }}
        >
          <Table sx={{ minWidth: 450 }} aria-label="simple table" stickyHeader size="small">
            <TableHead sx={{ whiteSpace: "nowrap" }}>
              <TableRow>
                {tableData?.columns.map(({ key, title }, index) => (
                  <TableCell
                    sx={{
                      maxWidth: "120px",
                      ...(index === 0 && {
                        position: "sticky",
                        left: 0,
                        zIndex: 5
                      })
                    }}
                    key={key}
                  >
                    {mapTitles(title)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData?.map(({ valueData }, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleRowClick(valueData)}
                  sx={{
                    bgcolor: "red",
                    cursor: "pointer",
                    ...(selectedDealName === valueData.deal && {
                      ".MuiTableCell-root": { bgcolor: "#dbe8f5" }
                    }),
                    ":hover": {
                      ".MuiTableCell-root": { bgcolor: "#dbe8f5" }
                    }
                  }}
                >
                  {tableData?.columns.map(({ key, postfix }, colIndex) => {
                    return (
                      <TableCell
                        key={key + index}
                        sx={{
                          maxWidth: "120px",
                          borderBottom: "1px solid #dbe8f5",
                          ...(key === "deal" && {
                            color: "#26A6AE"
                          }),
                          ...(colIndex === tableData.columns.length - 1 && { borderRight: "1px solid #dbe8f5" }),
                          ...(colIndex === 0 && {
                            backgroundColor: "white",
                            position: "sticky",
                            left: 0,
                            zIndex: 2
                          })
                        }}
                      >
                        <MaterialTooltip title={formatCellValue(valueData[key], postfix)} isShow={!!valueData[key]}>
                          <Typography
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {formatCellValue(valueData[key], postfix)}
                          </Typography>
                        </MaterialTooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* {customMenuOpen && (
        <ColumnsMenu
        customMenuOpen={customMenuOpen}
        onCloseWithRefetch={() => {
          action({
            dateRange: selectDateRange,
            groupBy: [["deal"]],
            currency,
            filters: pickBy(filters, (value) => value.length > 0),
            exchangeMethod,
            columns: ["Holding (Issued)", "Holding (FD)", "Investment", "adjNav", "moic", "Post Money", "Runway"]
          });
          setCustomMenuOpen(null);
        }}
        setCustomMenuOpen={setCustomMenuOpen}
        />
      )} */}
    </StyledStack>
  );
};
